import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";

import { createFinancialGoal, fetchBusinessDomain, fetchFinancialGoals } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmFinancialGoalCreatePage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  // Selected as array of indicatorName strings
  const [selectedNames, setSelectedNames] = useState([]);
  const [domainIndicators, setDomainIndicators] = useState([]);
  const [usedIndicatorNames, setUsedIndicatorNames] = useState(new Set());
  // 因果関係：この目標に影響を与える財務目標のID一覧
  const [sourceIds, setSourceIds] = useState([]);
  const [allFinancialGoals, setAllFinancialGoals] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [domain, existingSfs] = await Promise.all([
          fetchBusinessDomain(businessDomainId),
          fetchFinancialGoals(businessDomainId).catch(() => []),
        ]);
        setDomainIndicators(Array.isArray(domain?.indicators) ? domain.indicators : []);
        const goals = Array.isArray(existingSfs) ? existingSfs : [];
        setAllFinancialGoals(goals);
        const used = new Set(
          goals
            .flatMap((sf) => sf.indicators || [])
            .map((ind) => ind.indicatorName)
            .filter(Boolean)
        );
        setUsedIndicatorNames(used);
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 指標が選択されていない場合はエラー
    if (domainIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      // Map selected names back to full indicator objects
      const indicators = selectedNames
        .map((name) => domainIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);

      // 選択した因果関係を sources 形式に変換
      const sources = sourceIds
        .map((id) => allFinancialGoals.find((g) => g.successFactorId === id))
        .filter(Boolean)
        .map((g) => ({ successFactorId: g.successFactorId, successFactorName: g.successFactorName }));

      await createFinancialGoal({
        successFactorName,
        successFactorDescription,
        indicators,
        sources,
        businessDomainId,
        companyId,
      });
      notify.success(t("message.create.success"));
      navigate(`/design/${businessDomainId}/strategy-map/financial-goals`, { replace: true });
    } catch (err) {
      notify.apiError(err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("financialGoal.create.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          {/* 因果関係フィールド：影響を与える財務目標を選択 */}
          {allFinancialGoals.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.sources.label")}</InputLabel>
                <Select
                  multiple
                  value={sourceIds}
                  onChange={(e) => setSourceIds(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.sources.label")} />}
                >
                  {allFinancialGoals.map((g) => (
                    <MenuItem key={g.successFactorId} value={g.successFactorId}>
                      {g.successFactorName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {domainIndicators.length > 0 && (
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
                <Select
                  multiple
                  value={selectedNames}
                  onChange={(e) => setSelectedNames(e.target.value)}
                  input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
                >
                  {domainIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName} disabled={usedIndicatorNames.has(ind.indicatorName)}>
                      {ind.indicatorName}{usedIndicatorNames.has(ind.indicatorName) ? " （登録済み）" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.add")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/financial-goals`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
